import { Calendar, Power, Clock, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ApiService from '../../services/api';

const EnvironmentCard = ({ environment: initialEnvironment }) => {
    const navigate = useNavigate();
    const [environment, setEnvironment] = useState(initialEnvironment);
    const [isPolling, setIsPolling] = useState(false);
    const [pollingCount, setPollingCount] = useState(0);

    // Start polling for state updates if the environment is transitioning
    useEffect(() => {
        if (environment.state === 'Transitioning') {
            setIsPolling(true);
            const interval = setInterval(() => {
                fetchEnvironmentState();
                setPollingCount(prev => prev + 1);
            }, 5000); // Poll every 5 seconds

            return () => clearInterval(interval);
        } else {
            setIsPolling(false);
            setPollingCount(0);
        }
    }, [environment.state]);

    // If we've been polling for more than 2 minutes, stop polling
    useEffect(() => {
        if (pollingCount > 24) { // 24 * 5 seconds = 2 minutes
            setIsPolling(false);
        }
    }, [pollingCount]);

    const fetchEnvironmentState = async () => {
        try {
            const response = await ApiService.getEnvironmentState(environment.name);

            if (response.data && response.data.environment) {
                setEnvironment(response.data.environment);

                // If the state is no longer transitioning, stop polling
                if (response.data.environment.state !== 'Transitioning') {
                    setIsPolling(false);
                }
            }
        } catch (error) {
            console.error('Error fetching environment state:', error);
        }
    };

    const getStateColor = (state) => {
        switch (state) {
            case 'Ready':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'Transitioning':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Off':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (state) => {
        switch (state) {
            case 'Ready':
                return <Power className="h-3 w-3 mr-1 text-green-600" />;
            case 'Transitioning':
                return <Loader className="h-3 w-3 mr-1 text-yellow-600 animate-spin" />;
            case 'Off':
                return <Power className="h-3 w-3 mr-1 text-gray-600" />;
            default:
                return <Power className="h-3 w-3 mr-1 text-gray-600" />;
        }
    };

    const handleCardClick = (e) => {
        // Don't navigate if we're clicking on the last operation tooltip
        if (e.target.closest('.last-operation-tooltip')) {
            e.stopPropagation();
            return;
        }
        navigate(`/moneysaver/environment/${environment.name}`);
    };

    // Helper function to generate upcoming schedule display
    const generateUpcomingScheduleDisplay = (weeklySchedule) => {
        // If no schedule data
        if (!weeklySchedule) return <div className="text-sm text-gray-500 italic">No schedule configured</div>;

        // Get the next 3 days of scheduled events
        const today = new Date();
        const scheduleItems = [];

        for (let i = 0; i < 7 && scheduleItems.length < 6; i++) {
            const day = new Date(today);
            day.setDate(today.getDate() + i);

            const dayName = day.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
            const formattedDate = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

            // Get time slots for this day
            const timeSlots = weeklySchedule[dayName];
            if (timeSlots && timeSlots.length > 0) {
                // Sort slots by start time
                const sortedSlots = [...timeSlots].sort((a, b) => a.start.localeCompare(b.start));

                // Add a start and end event for each slot
                sortedSlots.forEach((slot, slotIndex) => {
                    // Only show up to 6 events (3 slots with start and end each)
                    if (scheduleItems.length < 6) {
                        const slotLabel = slot.label ? ` (${slot.label})` : '';

                        // Add start event
                        scheduleItems.push({
                            date: formattedDate,
                            time: formatTime(slot.start),
                            action: 'Start',
                            label: slotLabel
                        });
                    }

                    if (scheduleItems.length < 6) {
                        const slotLabel = slot.label ? ` (${slot.label})` : '';

                        // Add end event
                        scheduleItems.push({
                            date: formattedDate,
                            time: formatTime(slot.end),
                            action: 'Stop',
                            label: slotLabel
                        });
                    }
                });
            }
        }

        if (scheduleItems.length === 0) {
            return <div className="text-sm text-gray-500 italic">No upcoming scheduled actions</div>;
        }

        return scheduleItems.map((item, index) => (
            <div key={index} className="flex items-center text-sm">
                <Clock className="h-3 w-3 mr-2 text-gray-400" />
                <span className="w-24 text-gray-500">{item.date}</span>
                <span className="w-16 text-gray-500">{item.time}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    item.action === 'Start'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                    {item.action}
                </span>
                {item.label && (
                    <span className="ml-2 text-xs text-gray-500 truncate max-w-xs">
                        {item.label}
                    </span>
                )}
            </div>
        ));
    };

    // Helper to format time from 24h to 12h format
    const formatTime = (time24h) => {
        const [hours, minutes] = time24h.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    return (
        <div
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            onClick={handleCardClick}
        >
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">
                        {environment.name}
                    </h3>
                    <div className="flex items-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStateColor(environment.state)} flex items-center`}>
                            {getStatusIcon(environment.state)}
                            {environment.state}
                        </span>

                        {environment.lastOperation && (
                            <div className="relative ml-2 last-operation-tooltip group">
                                <span className="cursor-help text-gray-500 hover:text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </span>
                                <div className="absolute right-0 z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 w-64 bg-gray-800 text-white text-xs rounded py-2 px-3 shadow-lg">
                                    <p className="font-semibold mb-1">Last Operation:</p>
                                    <p>{environment.lastOperation}</p>
                                    <p className="mt-1 text-gray-300 text-xs italic">
                                        {new Date(environment.lastUpdated).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="border-t pt-4">
                    <h4 className="flex items-center text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="h-4 w-4 mr-2" />
                        Upcoming Schedule
                    </h4>

                    {/* Show upcoming schedule based on weeklySchedule data format */}
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                        {environment.weeklySchedule ?
                            generateUpcomingScheduleDisplay(environment.weeklySchedule) :
                            <div className="text-sm text-gray-500 italic">No schedule configured</div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EnvironmentCard;